import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { Users } from 'lucide-react';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { MockBadge } from '../components/MockBadge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/Select';
import { StatusBadge } from '../components/ui/StatusBadge';
import { Alert } from '../components/ui/Alert';
import { ExportContactsDialog } from '../components/ExportContactsDialog';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDate, formatDateWithYear } from '../lib/format';
import { AUTO_LABEL_NAMES } from '../lib/automationLabels';
import { useDebouncedValue } from '../lib/useDebouncedValue';
import { consentLabel, parseCodeList } from '../lib/contactAttributes';
import { ApiError } from '../types/api';
import type {
  AutoLabel,
  ContactsListResponse,
  ContactStatus,
  LabelsOverviewResponse,
} from '../types/api';

const STATUS_CONFIG: Record<
  ContactStatus,
  { icon: string; tone: 'success' | 'warning' | 'danger' | 'neutral'; label: string }
> = {
  active: { icon: '✓', tone: 'success', label: 'Active' },
  suppressed: { icon: '⛔', tone: 'neutral', label: 'Suppressed' },
};

const PAGE_SIZE = 20;
/** Search waits this long after the last keystroke before querying (review fix 25/9). */
export const SEARCH_DEBOUNCE_MS = 300;

const tagName = (label: string): string => AUTO_LABEL_NAMES[label as AutoLabel] ?? label;

export function KontakterListPage() {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<string | undefined>(undefined);
  // Keyset paging: page 0 has no cursor; Next pushes the server's `nextCursor`, Previous pops.
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors[cursors.length - 1];
  const resetPaging = () => setCursors([]);
  const [languageText, setLanguageText] = useState('');
  const [countryText, setCountryText] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const label = searchParams.get('label');
  // A label link from Customer tags reuses this mounted page; start from page 1 on a new label.
  const [pageLabel, setPageLabel] = useState(label);
  if (pageLabel !== label) {
    setPageLabel(label);
    resetPaging();
  }

  // The tag picker lists every tag with at least one contact (automatic and
  // manual), the same `GET /api/labels` the audience picker uses.
  const labels = useQuery({
    queryKey: ['labels'],
    queryFn: () => apiFetch<LabelsOverviewResponse>('/labels'),
  });
  const tagOptions = (labels.data?.counts ?? [])
    .map((c) => c.label)
    .sort((a, b) => tagName(a).localeCompare(tagName(b)));
  // A tag from the address (a Customer tags link) that has no contacts left still shows.
  if (label && !tagOptions.includes(label)) tagOptions.push(label);
  const setLabel = (next: string | null) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next) params.set('label', next);
      else params.delete('label');
      return params;
    });
    resetPaging();
  };

  const debouncedQ = useDebouncedValue(q, SEARCH_DEBOUNCE_MS);
  // One code each (the list endpoint filters on exactly one language and one country).
  // Anything that is not a plain two-letter code simply does not filter yet.
  const language = /^[A-Za-z]{2}$/.test(languageText.trim())
    ? (parseCodeList(languageText, 'lower')[0] ?? '')
    : '';
  const country = /^[A-Za-z]{2}$/.test(countryText.trim())
    ? (parseCodeList(countryText, 'upper')[0] ?? '')
    : '';

  const { isAdmin } = useAuth();
  // The export follows what the list shows: the same search text, status and tag.
  const exportFilters: Record<string, string> = {
    ...(q.trim() ? { q: q.trim() } : {}),
    ...(status ? { status } : {}),
    ...(label ? { label } : {}),
    ...(language ? { language } : {}),
    ...(country ? { country } : {}),
  };

  // `signal`: react-query aborts a superseded request when the key moves
  // on, so an older, slower search can never overwrite a newer one.
  const { data, isLoading, error } = useQuery({
    queryKey: ['contacts', debouncedQ, status, cursor, label, language, country],
    queryFn: ({ signal }) => {
      // The server rejects empty filter values, so only send what has a value.
      const params = new URLSearchParams();
      if (debouncedQ.trim()) params.set('q', debouncedQ.trim());
      if (status) params.set('status', status);
      if (label) params.set('label', label);
      if (language) params.set('language', language);
      if (country) params.set('country', country);
      if (cursor) params.set('cursor', cursor);
      params.set('limit', String(PAGE_SIZE));
      return apiFetch<ContactsListResponse>(`/contacts?${params.toString()}`, { signal });
    },
  });
  const errorMessage = error
    ? error instanceof ApiError && error.body.message
      ? error.body.message
      : 'The contacts could not be loaded. Please try again.'
    : null;

  const liveMessage = isLoading
    ? 'Loading contacts…'
    : data
      ? `${data.total} ${data.total === 1 ? 'contact' : 'contacts'} found`
      : '';

  return (
    <>
      <PageHeader
        title="Contacts"
        badge={<MockBadge />}
        description={
          isAdmin
            ? 'Exports the contacts matching the search, status and tag below. The file contains personal data (email addresses, names, consent records), so keep it safe and delete it when you are done. Every export is logged and needs a fresh 2FA code.'
            : undefined
        }
        actions={
          isAdmin ? (
            <>
              <Button variant="secondary" asChild>
                <Link to="/kontakter/import">Import CSV</Link>
              </Button>
              <ExportContactsDialog filters={exportFilters} />
            </>
          ) : null
        }
      />

      <div className="flex gap-2.5 items-end">
        <div className="flex-grow flex flex-col gap-1.5">
          <label htmlFor="contact-search" className="text-[13px] font-medium">
            Search by email or name
          </label>
          <Input
            id="contact-search"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              resetPaging();
            }}
            placeholder="e.g. gmx.de or Anna"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="status-filter" className="text-[13px] font-medium">
            Status
          </label>
          <Select
            {...(status !== undefined ? { value: status } : {})}
            onValueChange={(v) => {
              setStatus(v === 'all' ? undefined : v);
              resetPaging();
            }}
          >
            <SelectTrigger id="status-filter">
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {(Object.keys(STATUS_CONFIG) as ContactStatus[]).map((s) => (
                <SelectItem key={s} value={s}>
                  {STATUS_CONFIG[s].label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5 w-[180px]">
          <label htmlFor="tag-filter" className="text-[13px] font-medium">
            Tag
          </label>
          <Select value={label ?? 'all'} onValueChange={(v) => setLabel(v === 'all' ? null : v)}>
            <SelectTrigger id="tag-filter">
              <SelectValue placeholder="All tags" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All tags</SelectItem>
              {tagOptions.map((t) => (
                <SelectItem key={t} value={t}>
                  {tagName(t)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5 w-[110px]">
          <label htmlFor="language-filter" className="text-[13px] font-medium">
            Language
          </label>
          <Input
            id="language-filter"
            value={languageText}
            maxLength={2}
            placeholder="da"
            onChange={(e) => {
              setLanguageText(e.target.value);
              resetPaging();
            }}
          />
        </div>
        <div className="flex flex-col gap-1.5 w-[110px]">
          <label htmlFor="country-filter" className="text-[13px] font-medium">
            Country
          </label>
          <Input
            id="country-filter"
            value={countryText}
            maxLength={2}
            placeholder="DK"
            onChange={(e) => {
              setCountryText(e.target.value);
              resetPaging();
            }}
          />
        </div>
      </div>

      <div role="status" aria-live="polite" className="sr-only">
        {liveMessage}
      </div>

      {errorMessage ? <Alert>{errorMessage}</Alert> : null}

      {isLoading ? (
        <div className="text-muted" aria-hidden="true">
          Loading contacts…
        </div>
      ) : errorMessage ? null : (
        <>
          <div className="border border-border rounded-xl overflow-x-auto">
            <table className="w-full border-collapse text-left">
              <caption className="sr-only">Contacts</caption>
              <thead>
                <tr className="border-b border-border-soft text-muted text-xs">
                  <th scope="col" className="px-4 py-3 font-normal w-[130px]">
                    Status
                  </th>
                  <th scope="col" className="px-4 py-3 font-normal">
                    Email
                  </th>
                  <th scope="col" className="px-4 py-3 font-normal w-[90px]">
                    Language
                  </th>
                  <th scope="col" className="px-4 py-3 font-normal w-[80px]">
                    Country
                  </th>
                  <th scope="col" className="px-4 py-3 font-normal w-[150px]">
                    Consent
                  </th>
                  <th scope="col" className="px-4 py-3 font-normal w-[110px] text-right">
                    Last order
                  </th>
                  <th scope="col" className="px-4 py-3 font-normal w-[110px] text-right">
                    Created
                  </th>
                </tr>
              </thead>
              <tbody>
                {(data?.items.length ?? 0) === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-3">
                      <EmptyState
                        className="border-0 py-8"
                        icon={<Users />}
                        title="No contacts found"
                        description={
                          <>
                            Nothing matches the search and filters above. Someone who unsubscribed
                            is deleted from Contacts: look them up under{' '}
                            <Link to="/afmeldte">Unsubscribed</Link>.
                          </>
                        }
                      />
                    </td>
                  </tr>
                ) : (
                  data?.items.map((c) => {
                    const cfg = STATUS_CONFIG[c.status];
                    return (
                      // `relative` + the link's `after:inset-0` keeps the
                      // whole row clickable, as before the table markup.
                      <tr
                        key={c.id}
                        className="relative border-b border-border-soft last:border-b-0 hover:bg-bg"
                      >
                        <td className="px-4 py-3">
                          <StatusBadge tone={cfg.tone} icon={cfg.icon}>
                            {cfg.label}
                          </StatusBadge>
                        </td>
                        <td className="px-4 py-3">
                          <Link
                            to={`/kontakter/${c.id}`}
                            className="no-underline text-text after:absolute after:inset-0 after:content-['']"
                          >
                            {c.email}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-muted">{c.language ?? '—'}</td>
                        <td className="px-4 py-3 text-muted">{c.country ?? '—'}</td>
                        <td className="px-4 py-3 text-muted">
                          {consentLabel(c.consentCategory, c.orderSignupAt)}
                        </td>
                        <td className="px-4 py-3 text-right text-muted">
                          {c.lastOrderAt ? formatDateWithYear(c.lastOrderAt) : '—'}
                        </td>
                        <td className="px-4 py-3 text-right text-muted">
                          {formatDate(c.createdAt)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-muted text-[13px]">
              {data ? `${data.total} contacts in total` : ''}
            </span>
            <div className="ml-auto flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={cursors.length === 0}
                onClick={() => setCursors((s) => s.slice(0, -1))}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={!data?.nextCursor}
                onClick={() => {
                  const next = data?.nextCursor;
                  if (next) setCursors((s) => [...s, next]);
                }}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
